// Uygulama içi sekme şeridi (SEM-6336 / SEM-6724). Zustand 5, ADR-013 §8:
// burada yalnız UI durumu var, sunucu durumu TanStack Query'de kalır.
//
// SAKLAMA YERİ sessionStorage'dır, localStorage değil. Her TARAYICI sekmesi
// kendi uygulama-sekmesi kümesini taşır. localStorage kullanırsak iki tarayıcı
// sekmesi aynı listeyi paylaşır ve biri diğerinin sekmesini kapatır.
//
// Sabitlenmiş sekmeler korunur; yeni adres son sabitlenmemiş sekmenin yerini
// alır. Açık adrese dönmek yeni sekme üretmez. Açıkça sabitlemesi kaldırılan
// sekmeler yeni gezinmeye kadar korunur; gezinme tek önizlemeye indirger.
//
// Sabitleme KAPATMAYI engellemez. İkisi ayrı sorudur: sabitleme "sonraki
// gezinme bu sekmenin yerine yazmasın" der, çarpı "bu sekmeyi şimdi kaldır"
// der. Sabitlenmiş sekmeyi kapatılamaz yapmak, kullanıcıyı önce sabitlemeyi
// kaldırmaya zorlar ve tek kazancı yanlışlıkla kapatmayı önlemektir.
//
// EKRAN adresten geniştir: bir kabuğun alt görünümleri (`/assets/stock`) kökleri
// ile TEK sekmedir. Kümeyi burada elle saymayız, kabuğun kendi sekme listesinden
// türetiriz (`components/shell-tabs.ts`); ikinci liste bayatlar.
//
// ADRES TARAYICININ ADRESİDİR. Etkin sekmenin yolu URL'in kendisidir, bu yüzden
// derin bağlantı, geri/ileri ve tarayıcının kendi sekmeleri çalışmaya devam
// eder. Şerit tarayıcıyla yarışmaz, onun üstünde durur.

import { create } from "zustand";

import { MENU_SECTIONS, menuLinks } from "@/components/menu-config";
import { SHELL_TAB_GROUPS } from "@/components/shell-tabs";

const TABS_STORAGE_KEY = "novemai.tabs";

export interface AppTab {
  readonly id: string;
  /** Tam adres: pathname + varsa query. Sekmeye tıklamak buraya gider. */
  readonly path: string;
  /** Kayıt ekranının bildirdiği başlık. null ise manifestten türetilir. */
  readonly title: string | null;
  readonly pinned?: boolean;
}

interface PersistedTabs {
  readonly tabs: readonly AppTab[];
  readonly activeId: string | null;
}

function pathnameOf(path: string): string {
  const cut = path.indexOf("?");
  return cut === -1 ? path : path.slice(0, cut);
}

/** Manifestteki her adres → başlık anahtarı. Menü tek kaynaktır. */
const MENU_LABEL_BY_HREF: ReadonlyMap<string, string> = new Map(
  MENU_SECTIONS.flatMap((s) => menuLinks(s.items).map((i) => [i.href, i.labelKey] as const)),
);

/**
 * Sekmenin başlık anahtarı.
 *
 * Tam eşleşme yoksa en uzun ÖNEK eşleşmesi kullanılır: `/people/abc` sekmesi
 * kayıt kendi adını bildirene kadar "Personel" okur. Kök `/` önek sayılmaz,
 * yoksa her adresi eşleştirir ve her sekme "Ana Sayfa" olur.
 */
export function menuLabelKeyForPath(path: string): string | null {
  const pathname = pathnameOf(path);
  const exact = MENU_LABEL_BY_HREF.get(pathname);
  if (exact !== undefined) return exact;

  let best: string | null = null;
  let bestLength = 0;
  for (const [href, labelKey] of MENU_LABEL_BY_HREF) {
    if (href === "/") continue;
    if (pathname.startsWith(`${href}/`) && href.length > bestLength) {
      best = labelKey;
      bestLength = href.length;
    }
  }
  return best;
}

/**
 * Son sekme kapandığında gidilecek adres.
 *
 * Manifestten TÜRETİLİR, elle yazılmaz: `"/"` sabiti manifest değişirse
 * bayatlar ve bu dosyanın başlığı ikinci listeyi zaten yasaklıyor. Manifestte
 * `home` girdisi yoksa kök adrese düşeriz — şerit adres bulamadığı için
 * çalışmayı bırakmamalıdır.
 */
export const HOME_PATH: string =
  MENU_SECTIONS.flatMap((s) => menuLinks(s.items)).find((i) => i.key === "home")?.href ?? "/";

/** Kabuğun alt görünümü → ekran kökü. Küme `shell-tabs.ts`'ten türetilir. */
const SHELL_ROOT_BY_PATHNAME: ReadonlyMap<string, string> = new Map(
  SHELL_TAB_GROUPS.flatMap((group) => group.map((tab) => [tab.to, group[0]!.to] as const)),
);

/**
 * Sekme kimliği (SEM-7196).
 *
 * Bir kabuğun alt görünümleri kökleriyle AYNI kimliği taşır: `/assets/stock`
 * ile `/assets` tek şerit sekmesidir. Sekme çubuğu gerçek rotaya çevrildikçe
 * (`?tab=` yerine) kural olmadan üç ekranı gezen kullanıcı üç sekme biriktirir.
 *
 * Kayıt adresleri kendi kimliğini korur; sabitlenmemiş liste, kayıt
 * açıldığında önizleme kuralıyla yerini kayda bırakır.
 */
function tabKeyOf(path: string): string {
  const pathname = pathnameOf(path);
  return SHELL_ROOT_BY_PATHNAME.get(pathname) ?? pathname;
}

function readPersisted(): PersistedTabs {
  try {
    const raw = window.sessionStorage.getItem(TABS_STORAGE_KEY);
    if (raw === null) return { tabs: [], activeId: null };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return { tabs: [], activeId: null };
    const record = parsed as { tabs?: unknown; activeId?: unknown };
    const tabs = Array.isArray(record.tabs)
      ? record.tabs.filter((t): t is AppTab => {
          if (typeof t !== "object" || t === null) return false;
          const c = t as Record<string, unknown>;
          // `pinned` de denetlenir. Boole olmayan bir değer (bozuk ya da elle
          // yazılmış kayıt) okuma biçimine göre farklı cevap verir: doğruluk
          // sınaması onu sabitlenmiş sayar, kimlik sınaması saymaz. İki cevap
          // `syncLocation` içinde karşılaşırsa sekme listeden tamamen düşer.
          // Değer burada elenirse aşağıdaki tek okuma biçimi yeter.
          return (
            typeof c.id === "string" &&
            typeof c.path === "string" &&
            (c.title === null || typeof c.title === "string") &&
            (c.pinned === undefined || typeof c.pinned === "boolean")
          );
        })
      : [];
    const activeId = typeof record.activeId === "string" ? record.activeId : null;
    return { tabs, activeId: tabs.some((t) => t.id === activeId) ? activeId : null };
  } catch {
    return { tabs: [], activeId: null };
  }
}

function persist(state: PersistedTabs): void {
  try {
    window.sessionStorage.setItem(
      TABS_STORAGE_KEY,
      JSON.stringify({ tabs: state.tabs, activeId: state.activeId }),
    );
  } catch {
    /* en iyi çaba — bellekteki liste bu sayfa yüklemesi boyunca durur */
  }
}

interface TabsState {
  tabs: readonly AppTab[];
  activeId: string | null;
  /** Her adres değişiminde çağrılır: açık sekmeye geç ya da yeni sekme aç. */
  syncLocation: (path: string) => void;
  /**
   * Sekmeyi listeden çıkarır. Sabitlenmiş sekme de kapanır — sabitleme
   * "yerini koru" demektir, "kapatılamaz" değil.
   *
   * Son sekme de kapanır ve liste boşalır. Şerit boş listede hiçbir şey
   * çizmez; bu durumdan ÇIKMAK çağıranın işidir, çünkü gezinmeyi zaten o
   * başlatır (`__novemCloseTab`). Mağaza bunu garanti EDEMEZ: gezinme gelmezse
   * liste boş kalır.
   */
  closeTab: (id: string) => void;
  togglePin: (id: string) => void;
  /** Yalnız sıralamayı değiştirir; etkin ekran ve sabitleme durumu korunur. */
  moveTab: (id: string, targetId: string, edge: "before" | "after") => void;
  /** Etkin sekmenin başlığını kayıt ekranı bildirir. */
  setActiveTitle: (title: string) => void;
}

function makeId(): string {
  return crypto.randomUUID();
}

export const useTabsStore = create<TabsState>((set, get) => ({
  ...readPersisted(),

  syncLocation: (path: string): void => {
    set((state) => {
      const key = tabKeyOf(path);
      const matching = state.tabs.filter((t) => tabKeyOf(t.path) === key);
      const existing = matching.find((t) => t.pinned) ?? matching[0];
      if (existing !== undefined) {
        // Aynı ekranın filtresi ya da alt görünümü değişmiş olabilir (SEM-6350
        // filtreleri URL'de). Sekmenin adresini tazeleriz, yeni sekme açmayız;
        // böylece şerit sekmesine dönmek son bakılan alt görünüme döner.
        // Yeni bir kabuk grubu eski oturumda ayrı sekmeler olarak bulunabilir.
        // Aynı ekranın kopyalarını birleştirirken pini ve diğer ekranları koru.
        const distinct = state.tabs.filter(t => t.id === existing.id || tabKeyOf(t.path) !== key);
        const preview = existing.pinned ? distinct.filter(t => !t.pinned).at(-1) : existing;
        const tabs = distinct.filter(t => t.pinned || t.id === preview?.id)
          .map(t => t.id === existing.id && t.path !== path ? { ...t, path } : t);
        const next = { tabs, activeId: existing.id };
        persist(next);
        return next;
      }
      const tab: AppTab = { id: makeId(), path, title: null, pinned: false };
      // Kullanıcı önizlemeyi başka bir yere taşımış olabilir. Yeni adres o
      // yuvayı devralır; her gezinmede önizlemeyi şeridin sonuna fırlatmayız.
      const preview = state.tabs.filter(t => !t.pinned).at(-1);
      const tabs = preview === undefined
        ? [...state.tabs, tab]
        : state.tabs.flatMap(t => t.pinned ? [t] : t.id === preview.id ? [tab] : []);
      const next = { tabs, activeId: tab.id };
      persist(next);
      return next;
    });
  },

  togglePin: (id: string): void => {
    set(state => {
      const next = { tabs: state.tabs.map(tab => tab.id === id ? { ...tab, pinned: !tab.pinned } : tab), activeId: state.activeId };
      persist(next);
      return next;
    });
  },

  moveTab: (id, targetId, edge): void => {
    set(state => {
      const source = state.tabs.find(tab => tab.id === id);
      if (source === undefined || id === targetId || !state.tabs.some(tab => tab.id === targetId)) return state;
      const tabs = state.tabs.filter(tab => tab.id !== id);
      const index = tabs.findIndex(tab => tab.id === targetId) + (edge === "after" ? 1 : 0);
      tabs.splice(index, 0, source);
      if (tabs.every((tab, at) => tab === state.tabs[at])) return state;
      const next = { tabs, activeId: state.activeId };
      persist(next);
      return next;
    });
  },

  closeTab: (id: string): void => {
    const state = get();
    const at = state.tabs.findIndex((t) => t.id === id);
    if (at === -1) return;
    const tabs = state.tabs.filter((t) => t.id !== id);
    // Kapanan sekme etkinse komşusu etkin olur: önce sağdaki, yoksa soldaki.
    // Liste boşaldıysa etkin sekme yoktur.
    const activeId =
      id === state.activeId ? (tabs[Math.min(at, tabs.length - 1)]?.id ?? null) : state.activeId;
    const next = { tabs, activeId };
    persist(next);
    set(next);
  },

  setActiveTitle: (title: string): void => {
    set((state) => {
      const active = state.tabs.find((t) => t.id === state.activeId);
      if (active === undefined || active.title === title) return state;
      const next = {
        tabs: state.tabs.map((t) => (t.id === active.id ? { ...t, title } : t)),
        activeId: state.activeId,
      };
      persist(next);
      return next;
    });
  },
}));

/**
 * Oturum bittiğinde şeridi siler (SEM-8116).
 *
 * SEM-7243'ten beri bir kayıt sekmesi öznesinin ADINI taşır (bir kişinin adı,
 * bir zimmet etiketi). Ortak bir iş istasyonunda çıkış yönlendirmesi aynı
 * tarayıcı sekmesinde kaldığı için sessionStorage hayatta kalır: anahtar
 * silinmezse sıradaki kullanıcı, herhangi bir yetki denetiminden ÖNCE
 * öncekinin kayıt adlarını okur. Bellekteki durum da sıfırlanır, yoksa şerit
 * bu sayfa yüklemesi boyunca eski listeyi göstermeye devam eder.
 */
export function clearTabs(): void {
  try {
    window.sessionStorage.removeItem(TABS_STORAGE_KEY);
  } catch {
    /* en iyi çaba — bellekteki liste yine de sıfırlanır */
  }
  useTabsStore.setState({ tabs: [], activeId: null });
}
